import { Fragment, useId } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../icons';
import { menuItems, useMenu } from './useMenu';

/**
 * Table conventions (CO-88, audit TB1, TB3, TB5):
 *
 * - a table always sits in `TableScroll`, so a wide table scrolls inside
 *   its own box and never moves the page sideways;
 * - `.table` rows are compact (8/12 px cells); row headers use `th scope=row`;
 * - the trailing actions of a row are `RowActions`: side by side, small
 *   secondary buttons, and when a row has more than two actions, the rest
 *   go in an `ActionMenu`.
 */

export function TableScroll({ label, children, testId, className, busy }: { label?: string; children: ReactNode; testId?: string; className?: string; busy?: boolean }) {
  // A labelled region can be scrolled from the keyboard once focused.
  return (
    <div className={className ? `table-scroll ${className}` : 'table-scroll'} role={label ? 'region' : undefined} aria-label={label} aria-busy={busy} tabIndex={label ? 0 : undefined} data-testid={testId}>
      {children}
    </div>
  );
}

/**
 * Table (CO-171): a table the way every list in the dashboard is built — a
 * scroll region named by `label` ("Deployment attempts"; the table carries the
 * same name, so it is found by name too), so a wide table
 * scrolls in its own box and never moves the page sideways, and the table
 * inside it with the shared row styles. `compact` is the dense variant for
 * cards and drawers. `className` styles the table itself (column widths),
 * `wrapperClassName` the region around it. Use `ResponsiveTable` for a list
 * of records that becomes cards on a phone.
 */
export function Table({
  label,
  compact,
  className,
  wrapperClassName,
  testId,
  tableTestId,
  busy,
  keepRoles,
  children,
}: {
  label: string;
  compact?: boolean;
  className?: string;
  wrapperClassName?: string;
  /** On the scroll region. */
  testId?: string;
  /** On the table. */
  tableTestId?: string;
  busy?: boolean;
  /** For a table a stylesheet turns into cards on a phone (`display: block`
   * strips a table's semantics in some browsers): `role="table"` keeps them,
   * and the rows and cells carry their own roles. */
  keepRoles?: boolean;
  children: ReactNode;
}) {
  return (
    <TableScroll label={label} className={wrapperClassName} testId={testId} busy={busy}>
      <table
        className={`table${compact ? ' table--compact' : ''}${className ? ` ${className}` : ''}`}
        aria-label={label}
        role={keepRoles ? 'table' : undefined}
        data-testid={tableTestId}
      >
        {children}
      </table>
    </TableScroll>
  );
}

export function RowActions({ children }: { children: ReactNode }) {
  return <div className="row-actions">{children}</div>;
}

export interface MenuAction {
  label: string;
  to?: string;
  onClick?: () => void;
  /** Destructive: shown in the danger colour, last. */
  danger?: boolean;
  disabled?: boolean;
  testId?: string;
}

/**
 * ActionMenu: the overflow ("…") menu of a header or a row. A real menu
 * button; the keyboard and pointer behaviour is `useMenu`, shared with the
 * account menu: Enter, Space or ArrowDown opens it on the first item, ArrowUp
 * on the last, arrows move, Escape closes it and returns to the button, Tab
 * or a click outside closes it.
 */
export function ActionMenu({
  label,
  items,
  trigger,
  size = 'md',
  testId,
}: {
  /** Names the button and the menu ("More project actions"). */
  label: string;
  items: MenuAction[];
  /** Visible trigger text; without it the trigger is a "…" icon button. */
  trigger?: ReactNode;
  size?: 'sm' | 'md';
  testId?: string;
}) {
  const { open, close, rootRef, triggerRef, panelRef, triggerProps } = useMenu({ items: menuItems });
  const menuId = useId();

  const ordered = [...items.filter((item) => !item.danger), ...items.filter((item) => item.danger)];

  return (
    <div className="action-menu" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={trigger ? `button button--secondary${size === 'sm' ? ' button--small' : ''}` : `icon-button${size === 'sm' ? ' icon-button--sm' : ''}`}
        {...triggerProps}
        aria-controls={open ? menuId : undefined}
        aria-label={trigger ? undefined : label}
        title={trigger ? undefined : label}
        data-testid={testId}
      >
        {trigger ?? <Icon name="more" size={16} />}
        {trigger ? <Icon name="chevron-down" size={16} /> : null}
      </button>
      {open ? (
        <div id={menuId} className="action-menu__menu" role="menu" aria-label={label} ref={panelRef}>
          {ordered.map((item, index) => {
            const className = `action-menu__item${item.danger ? ' action-menu__item--danger' : ''}`;
            const divider = item.danger && index > 0 && !ordered[index - 1]!.danger ? <div className="action-menu__divider" role="separator" /> : null;
            if (item.to && !item.disabled) {
              return (
                <Fragment key={item.label}>
                  {divider}
                  <Link role="menuitem" tabIndex={-1} className={className} to={item.to} onClick={() => close(false)} data-testid={item.testId}>
                    {item.label}
                  </Link>
                </Fragment>
              );
            }
            return (
              <Fragment key={item.label}>
                {divider}
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  className={className}
                  aria-disabled={item.disabled || undefined}
                  onClick={() => {
                    if (item.disabled) return;
                    close(true);
                    item.onClick?.();
                  }}
                  data-testid={item.testId}
                >
                  {item.label}
                </button>
              </Fragment>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
